import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AlertsTable.module.scss";
import CustomTable from "../../common/CustomTable";
import SwitchText from "../../common/hookForm/SwitchText";
import {
  IAlert,
  EAlertStatus,
} from "../../../setup/api/services/alerts/models";
import { useAlertsStore } from "../../../store/alerts";
import { useAlertStore } from "../../../store/alertStore";
import EmptyIcon from "../../../assets/images/svg/empty.svg";
import DeleteAlertModal, { DeleteAlertModalRef } from "../DeleteAlertModal";
import CreateAlertModal, { CreateAlertModalRef } from "../CreateAlertModal";
import TestAlertModal, { TestAlertModalRef } from "../TestAlertModal";

const AlertsTable: React.FC = () => {
  const { t } = useTranslation();
  const alerts = useAlertsStore((s) => s.alerts);
  const loading = useAlertsStore((s) => s.loading);
  const error = useAlertsStore((s) => s.error);
  const fetchAlerts = useAlertsStore((s) => s.fetchAlerts);
  const toggleAlertStatus = useAlertsStore((s) => s.toggleAlertStatus);
  const deleteAlert = useAlertsStore((s) => s.deleteAlert);
  const { showError, showSuccess } = useAlertStore();
  const deleteModalRef = useRef<DeleteAlertModalRef>(null);
  const createAlertModalRef = useRef<CreateAlertModalRef>(null);
  const testAlertModalRef = useRef<TestAlertModalRef>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Load alerts on component mount
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Show error alert when fetching fails
  useEffect(() => {
    if (error) {
      showError(t("alert_fetch_error"), t("alert_fetch_error_message"));
    }
  }, [error, showError, t]);

  const handleStatusToggle = async (id: string) => {
    setTogglingId(id);
    try {
      const updatedAlert = await toggleAlertStatus(id);
      if (updatedAlert) {
        const messageKey =
          updatedAlert.status === EAlertStatus.ACTIVE
            ? "alert_now_active"
            : "alert_now_inactive";
        showSuccess(t(messageKey, { id: updatedAlert.id }));
      }
    } catch (err) {
      showError(
        t("alert_status_update_failed"),
        t("alert_status_update_error_message")
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleEdit = (id: string) => {
    createAlertModalRef.current?.openModal(id);
  };

  const handleDelete = (id: string) => {
    setSelectedAlertId(id);
    deleteModalRef.current?.openModal();
  };

  const handleTestAlert = (id: string) => {
    testAlertModalRef.current?.openModal(id);
  };

  const confirmDelete = async () => {
    if (selectedAlertId) {
      try {
        await deleteAlert(selectedAlertId);
        showSuccess(t("alert_deleted_successfully"));
        await fetchAlerts();
        setSelectedAlertId(null);
      } catch (err) {
        showError(t("alert_delete_failed"));
        throw err; // Re-throw to let modal handle the error state
      }
    }
  };

  const columns = [
    {
      key: "reorder",
      title: `${t("lbl_profile_list_address")}`,
      width: "30%",
      align: "right" as const,
      render: (_, record: IAlert) => (
        <p className={styles["message"]}>{record?.messageAr}</p>
      ),
    },
    {
      key: "action_button",
      title: `${t("alert_action_button")}`,
      width: "20%",
      render: (_, record: IAlert) => (
        <p className={styles["message"]}>{record?.cta?.label ?? ""}</p>
      ),
    },
    {
      key: "platforms",
      title: `${t("alert_platforms")}`,
      width: "20%",
      render: (_, record: IAlert) => (
        <div className={styles.platforms_container}>
          {record.surfaces.map((surface, index) => {
            // Format platform names: iOS stays as "iOS", others get first letter capitalized
            const formattedSurface =
              surface === "IOS"
                ? "iOS"
                : surface.charAt(0).toUpperCase() +
                  surface.slice(1).toLowerCase();

            return (
              <span key={index} className={styles.platform_tag}>
                {formattedSurface}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: "status",
      title: `${t("alert_condition")}`,
      width: "10%",
      align: "center" as const,
      render: (_, record: IAlert) => {
        const isActive = record.status === EAlertStatus.ACTIVE;
        const isToggling = togglingId === record.id;

        return (
          <div className={styles.status_container}>
            <SwitchText
              value={isActive}
              disabled={loading || isToggling}
              onChange={() => handleStatusToggle(record.id)}
            />
          </div>
        );
      },
    },
    {
      key: "actions",
      title: `${t("procedures")}`,
      width: "20%",
      render: (_, record: IAlert) => (
        <div className={styles.actions_container}>
          <p
            className={styles.offer_button}
            onClick={() => handleTestAlert(record.id)}>
            {t("offer")}
          </p>
          <p
            className={styles.edit_button}
            onClick={() => handleEdit(record.id)}>
            {t("edit")}
          </p>
          <p
            className={styles.delete_button}
            onClick={() => handleDelete(record.id)}>
            {t("delete")}
          </p>
        </div>
      ),
    },
  ];

  // Shimmer loading component
  const ShimmerRows = () => (
    <div className={styles.shimmer_container}>
      {[...Array(5)].map((_, index) => (
        <div key={index} className={styles.shimmer_row}>
          {columns.map((column) => (
            <div
              key={column.key}
              className={styles.shimmer_cell}
              style={{ width: column.width }}>
              <div className={styles.shimmer_content} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className={styles.empty_state}>
      <img src={EmptyIcon} alt="No alerts" className={styles.empty_icon} />
      <h3 className={styles.empty_title}>{t("alert_no_alerts_title")}</h3>
      <p className={styles.empty_description}>
        {t("alert_no_alerts_description")}
      </p>
      <button
        className={styles.add_alert_button}
        onClick={() => createAlertModalRef.current?.openModal()}>
        + {t("alert_add_new")}
      </button>
    </div>
  );

  return (
    <>
      <CustomTable<IAlert>
        columns={columns}
        data={alerts}
        loading={loading}
        loadingComponent={<ShimmerRows />}
        className={styles.alerts_table}
      />
      {!loading && (alerts.length === 0 || error) && <EmptyState />}
      <DeleteAlertModal ref={deleteModalRef} onConfirm={confirmDelete} />
      <CreateAlertModal ref={createAlertModalRef} title="" />
      <TestAlertModal ref={testAlertModalRef} />
    </>
  );
};

export default AlertsTable;
